import moment from 'moment-timezone';
import { collect } from 'collect.js';

class HelperMethods {
  formatDate(date, format) {
    try {
      if (!date) {
        return '';
      }

      if (format) {
        return moment(date).format(format);
      }

      return moment(date).format('DD.MM.YYYY');
    } catch (error) {
      return '';
    }
  }

  formatDateTime(date) {
    return this.formatDate(date, 'DD.MM.YYYY HH:mm');
  }

  /**
   * returns the relative time from now. E.g. 3 weeks ago
   */
  formatRelativeTime(date, withoutSuffix = true) {
    try {
      if (!date) {
        return '';
      }

      return moment(date).fromNow(withoutSuffix);
    } catch (error) {
      return '';
    }
  }

  // `currency` and `locale` (the region's `number_locale`, e.g. "de-DE") are both
  // required — no currency->locale guessing. A missing region is a bug to
  // surface loudly, not paper over with a guessed locale.
  formatCurrency(amount, currency, locale, options = {}) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        ...options,
      }).format(amount);
    } catch (error) {
      return amount;
    }
  }

  getCurrencySymbol(currency, locale) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency').value;
    } catch (e) {
      return currency;
    }
  }

  // CLDR/Unicode date pattern (from the region's `date_format`) -> moment token.
  // Uppercases only `y` and `d` runs (moment's year/day-of-month tokens are
  // uppercase; CLDR's are lowercase) and leaves `M`/`H`/`h`/`m`/`s` and every
  // separator untouched, since those already match between the two dialects.
  cldrToMoment(pattern) {
    return pattern.replace(/y+|d+/g, (run) => run.toUpperCase());
  }

  // Must not rely on `this`: call sites pass this method detached (formatter={HelperMethods.formatPercentage}).
  formatPercentage(percentage) {
    const value =
      typeof percentage === 'number' && Number.isFinite(percentage) ? Math.round(percentage * 100) / 100 : percentage;

    return `${value}%`;
  }

  round(num) {
    return Math.round(num * 100) / 100;
  }

  percentagePart(a, b, fractionDigits = 2) {
    if (a === 0 || b === 0) {
      return 0;
    }

    const result = (a / b) * 100;

    return parseFloat(result.toFixed(fractionDigits));
  }

  randomString(length = 6) {
    const string = btoa(Math.random());

    return string.slice(string.length - 2 - length, string.length - 2).toUpperCase();
  }

  getFilenameFromUrl(url) {
    try {
      return url.split('/').lastItem;
    } catch {
      return '';
    }
  }

  parseFloat(value) {
    if (typeof value !== 'string') {
      return value;
    }

    return parseFloat(value.replace(',', '.'));
  }

  empty(value) {
    return !value || value === undefined || (Array.isArray(value) && value.length === 0);
  }

  min(...values) {
    return collect(values)
      .filter((item) => !!item)
      .min();
  }
}

export default new HelperMethods();
